import type { GetMarketplaceJobsResponseDto } from "@workspace/dto/job/get-marketplace-jobs";
import { Button } from "@workspace/ui/components/button";
import { useIsMobile } from "@workspace/ui/hooks/use-mobile";
import { cn } from "@workspace/ui/lib/utils";
import { useCallback, useMemo, useRef, useState } from "react";
import { Icons } from "@/components/icons";
import {
  ListingsBottomSheet,
  type ListingsBottomSheetHandle,
} from "./listings-bottom-sheet";
import { ListingsPanel } from "./listings-panel";
import { MarketplaceMap, type MarketplaceMapHandle } from "./map";
import { isInBounds, type MapBounds } from "./map-bounds";
import type { MarketplaceJob } from "./use-cluster";

const COLLAPSED_STORAGE_KEY = "vacwork:job-listings-collapsed";

type JobListingsLayoutProps = {
  jobs: GetMarketplaceJobsResponseDto["jobs"] | undefined;
  loading: boolean;
};

export function JobListingsLayout({ jobs, loading }: JobListingsLayoutProps) {
  const isMobile = useIsMobile();
  const [bounds, setBounds] = useState<MapBounds | undefined>(undefined);
  const [userLocation, setUserLocation] = useState<
    { latitude?: number; longitude?: number } | undefined
  >(undefined);

  // Phase 5 sync state. Lifted here so the list (panel + sheet) and the map
  // share one source of truth for selection + hover.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Mobile sheet handle — exposes a snapTo() the floating badge uses to
  // expand the bottom sheet from peek.
  const sheetRef = useRef<ListingsBottomSheetHandle | null>(null);
  const mapRef = useRef<MarketplaceMapHandle | null>(null);

  // Memoized handlers so ListingsPanel + ListingList can use React.memo
  // effectively (phase 6 perf).
  const handleSelect = useCallback((job: MarketplaceJob) => {
    setSelectedId(job.id);
    mapRef.current?.openSelectedJob(job);
  }, []);
  const handleHover = useCallback((id: string | null) => setHoveredId(id), []);
  const handleSelectedIdChange = useCallback((id: string | null) => {
    setSelectedId(id);
    if (!id) {
      mapRef.current?.closeSelectedMarker();
    }
  }, []);
  const handleHoveredIdChange = useCallback(
    (id: string | null) => setHoveredId(id),
    []
  );

  // Phase 6: desktop panel collapse toggle with localStorage persistence.
  const [listingsCollapsed, setListingsCollapsed] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
  });

  const toggleListingsCollapsed = useCallback(() => {
    setListingsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next));
      } catch {
        /* localStorage unavailable */
      }
      return next;
    });
  }, []);

  const jobsInView = useMemo(() => {
    if (!bounds) {
      return jobs ?? [];
    }
    return (jobs ?? []).filter((job) =>
      isInBounds(
        job.address.location.coordinates[0] as number,
        job.address.location.coordinates[1] as number,
        bounds
      )
    );
  }, [jobs, bounds]);

  // On mobile the NestedRoot job-detail drawer opens on a job selected from
  // the list. Resolved from the full `jobs` array (not just in-view) so it
  // still works if a job scrolls out of the windowed list.
  const selectedJob = useMemo<MarketplaceJob | null>(() => {
    if (!selectedId) {
      return null;
    }
    return (jobs ?? []).find((j) => j.id === selectedId) ?? null;
  }, [selectedId, jobs]);

  return (
    <div className="relative flex h-full min-h-0 flex-1">
      <div
        className={cn(
          // grid-rows-[minmax(0,1fr)] pins the single row to the container
          // height so a long listings column can't grow the row past the
          // viewport — the list's own overflow-y-auto is then the scroller.
          "grid size-full min-h-0 grid-cols-1 grid-rows-[minmax(0,1fr)] overflow-hidden",
          listingsCollapsed
            ? "md:grid-cols-[3rem_1fr]"
            : "md:grid-cols-[minmax(22rem,26rem)_1fr]"
        )}
      >
        {/* Desktop list panel. Renders an inline expand affordance when collapsed
            so users don't have to hunt for the toggle on the map. */}
        {listingsCollapsed ? (
          <button
            aria-label="Expand job listings panel"
            className="hidden h-full w-full flex-col items-center justify-center gap-2 border-border border-r bg-muted/30 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:flex"
            onClick={toggleListingsCollapsed}
            title="Show listings"
            type="button"
          >
            <span className="-rotate-90 whitespace-nowrap font-medium text-xs uppercase tracking-wider">
              Listings
            </span>
          </button>
        ) : (
          <div className="hidden h-full min-h-0 overflow-hidden md:block">
            <ListingsPanel
              collapsed={false}
              hoveredId={hoveredId}
              jobs={jobsInView}
              loading={loading}
              onHover={handleHover}
              onSelect={handleSelect}
              onToggleCollapse={toggleListingsCollapsed}
              selectedId={selectedId}
              userLatitude={userLocation?.latitude}
              userLongitude={userLocation?.longitude}
            />
          </div>
        )}

        {/* Map column — full-bleed on mobile, right pane on desktop. */}
        <div className="absolute inset-0 min-h-0 md:relative md:inset-auto md:h-full">
          {/* Collapse toggle, inset at top-left of the map. */}
          <Button
            className="absolute top-3 left-3 z-20 hidden md:inline-flex"
            onClick={toggleListingsCollapsed}
            size="icon"
            variant="outline"
          >
            {listingsCollapsed ? (
              <Icons.panelLeftOpen className="size-4" />
            ) : (
              <Icons.panelLeftClose className="size-4" />
            )}
          </Button>
          <MarketplaceMap
            hoveredId={hoveredId}
            jobs={jobs}
            onBoundsChange={setBounds}
            onHoveredIdChange={handleHoveredIdChange}
            onSelectedIdChange={handleSelectedIdChange}
            onUserLocationChange={setUserLocation}
            ref={mapRef}
            selectedId={selectedId}
            userLocation={userLocation}
          />
        </div>
      </div>

      {/*
        Mobile bottom sheet overlays the map. modal={false} keeps the map
        pointer-interactive at peek/mid; the NestedRoot job-detail drawer
        mounts inside the sheet's Drawer context for coordinated scale-back.
      */}
      {isMobile && (
        <ListingsBottomSheet
          hoveredId={hoveredId}
          jobs={jobsInView}
          loading={loading}
          onHover={handleHover}
          onSelect={handleSelect}
          onSelectedJobClose={() => handleSelectedIdChange(null)}
          ref={sheetRef}
          selectedId={selectedId}
          selectedJob={selectedJob}
          userLatitude={userLocation?.latitude}
          userLongitude={userLocation?.longitude}
        />
      )}
    </div>
  );
}
